/**
 * Pure view model for the `/invites` list pane, including the rejected-row
 * overlay.
 *
 * The overlay exists because `useInviteDecline`'s generated `onSuccess`
 * invalidates the pending-only invite list about 150ms after the mutation
 * settles, and the refetched list no longer contains the declined invite.
 * R4 requires the card to stay on screen until the fan navigates away, so
 * this module merges a component-local map of rejected rows over whatever
 * the server most recently returned. The map is component-local and dies
 * when the pane unmounts, which is what makes navigating away and back show
 * a clean list. The server's own order (`createdAt: -1`) is authoritative;
 * this module never re-sorts it.
 */
import {
  poolMarkInitials,
  poolMarkTone,
  type typePoolMarkTone,
} from "../pools/poolsListState";
import { inviteDeclineCopy } from "./inviteOutcomeCopy";

export type typeInviteRow = {
  bannerImageUrl?: string | null | undefined;
  creatorHandle?: string | null | undefined;
  inviteId: string;
  poolId: string;
  poolName: string;
  invitedByName: string;
  sportLabel: string;
  contestLabel: string;
  sentAtLabel: string;
  /**
   * ISSUE-1426. "link" for a pool the fan opened from a link or code and
   * has not joined; the server's marker, verbatim. Absent reads as an
   * invite someone sent.
   */
  source?: "addressed" | "link" | undefined;
};

/** ISSUE-1426. A link row's byline and the line it leaves after Not now. */
export const LINK_ROW_NOT_NOW_LABEL = "Not now";
export const LINK_ROW_DISMISSED_MESSAGE =
  "Off your list. Open the link again any time to bring it back.";
export function linkRowInviterLine(invitedByName: string): string {
  return `You opened a link from ${invitedByName}`;
}

export type typeRejectedEntry = { row: typeInviteRow; index: number };

export type typeInviteCardModel = {
  bannerImageUrl?: string | null | undefined;
  creatorHandle?: string | null | undefined;
  inviteId: string;
  poolId: string;
  poolName: string;
  initials: string;
  markTone: typePoolMarkTone;
  /** `${invitedByName} invited you` -- no trailing period, per the frame. */
  inviterLine: string;
  /** The server's string, verbatim. Never parsed, never reformatted. */
  sentAtLabel: string;
  /** [sportLabel, contestLabel], both verbatim. Never a league. */
  facts: readonly string[];
  phase: "waiting" | "rejected";
  /** Set only when phase is "rejected". Null otherwise. */
  rejectedMessage: string | null;
  /**
   * ISSUE-1426. A pool the fan opened from a link: the second action reads
   * Not now and needs no confirm, because it only drops their own bookmark.
   */
  isFromLink: boolean;
};

export type typeInvitesListState =
  | { phase: "loading" }
  | { phase: "failed" }
  | { phase: "empty" }
  | { phase: "ready"; cards: readonly typeInviteCardModel[] };

export function toInviteCardModel(
  row: typeInviteRow,
  phase: "waiting" | "rejected",
): typeInviteCardModel {
  return {
    inviteId: row.inviteId,
    bannerImageUrl: row.bannerImageUrl,
    creatorHandle: row.creatorHandle,
    poolId: row.poolId,
    poolName: row.poolName,
    initials: poolMarkInitials(row.poolName),
    markTone: poolMarkTone(row.poolId),
    inviterLine:
      row.source === "link"
        ? linkRowInviterLine(row.invitedByName)
        : `${row.invitedByName} invited you`,
    sentAtLabel: row.sentAtLabel,
    facts: [row.sportLabel, row.contestLabel],
    phase,
    rejectedMessage:
      phase !== "rejected"
        ? null
        : row.source === "link"
          ? LINK_ROW_DISMISSED_MESSAGE
          : inviteDeclineCopy("declined").message,
    isFromLink: row.source === "link",
  };
}

export function resolveInvitesListState(input: {
  isLoading: boolean;
  hasFailed: boolean;
  items: readonly typeInviteRow[] | null;
  rejected: ReadonlyMap<string, typeRejectedEntry>;
}): typeInvitesListState {
  if (input.rejected.size > 0) {
    const items = input.items ?? [];
    const serverCards = items.map((row) =>
      toInviteCardModel(
        row,
        input.rejected.has(row.inviteId) ? "rejected" : "waiting",
      ),
    );

    const orphans = [...input.rejected.values()]
      .filter(
        (entry) => !items.some((row) => row.inviteId === entry.row.inviteId),
      )
      .sort((a, b) => a.index - b.index);

    for (const orphan of orphans) {
      const insertAt = Math.min(orphan.index, serverCards.length);
      serverCards.splice(
        insertAt,
        0,
        toInviteCardModel(orphan.row, "rejected"),
      );
    }

    return { phase: "ready", cards: serverCards };
  }

  if (input.hasFailed) {
    return { phase: "failed" };
  }
  if (input.isLoading) {
    return { phase: "loading" };
  }
  if (input.items === null || input.items.length === 0) {
    return { phase: "empty" };
  }

  return {
    phase: "ready",
    cards: input.items.map((row) => toInviteCardModel(row, "waiting")),
  };
}
